import { useState, useEffect, useMemo } from 'react';
import { Loader2, RefreshCw, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Plus, Trash2, AlertTriangle, X, CircleEllipsis, Pencil } from 'lucide-react';
import { createPortal } from 'react-dom';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { useClientLevel } from '../../hooks/useClientLevel';
import ScrollContainer from 'react-indiana-drag-scroll';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import DBConnectionModal from '../InternalScan/InternalScanDashboard/DBConnectionModal';
import ColumnVisibilityDropdown, { type ColumnOption } from '../../components/ColumnVisibilityDropdown';

interface DeleteConfirmationModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    connectionName: string | undefined;
    isDeleting: boolean;
}

export type AgentColumnKey = 'ip_address' | 'hostname' | 'status' | 'connection_id' | 'db_monitoring';
export type ConnColumnKey = 'name' | 'db_type' | 'host' | 'port' | 'role' | 'agent_ids' | 'actions';

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({ open, onClose, onConfirm, connectionName, isDeleting }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-3">
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-w-xs w-full overflow-hidden transform transition-all animate-in zoom-in duration-200">
                <div className="p-4">
                    <div className="flex justify-between items-start mb-3">
                        <div className="p-2 bg-red-100 dark:bg-red-500/20 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                        </div>
                        <button onClick={onClose} disabled={isDeleting} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors disabled:opacity-50 cursor-pointer">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Delete DB Connection?</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-[11px] leading-relaxed mb-4">
                        Are you sure you want to delete <span className="text-gray-900 dark:text-white font-medium">"{connectionName}"</span>?
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            disabled={isDeleting}
                            className="flex-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-[11px] font-medium transition-colors disabled:opacity-50 cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={isDeleting}
                            className="flex-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-[11px] font-medium transition-colors flex items-center justify-center disabled:opacity-50 cursor-pointer"
                        >
                            {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Delete'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DbSettings = () => {
    const level = useClientLevel();
    const [activeTab, setActiveTab] = useState<"agents" | "connections">("agents");

    // --- Agents State ---
    const [agentData, setAgentData] = useState<any[]>([]);
    const [loadingAgents, setLoadingAgents] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(30);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Agents Column Visibility
    const [agentVisibleColumns, setAgentVisibleColumns] = useState<Record<AgentColumnKey, boolean>>({
        ip_address: true,
        hostname: true,
        status: true,
        connection_id: true,
        db_monitoring: true,
    });

    const agentColumnOptions: ColumnOption<AgentColumnKey>[] = useMemo(() => [
        { key: 'ip_address', label: 'IP Address' },
        { key: 'hostname', label: 'Hostname' },
        { key: 'status', label: 'Status' },
        { key: 'connection_id', label: 'Connection ID' },
        { key: 'db_monitoring', label: 'DB Monitoring' },
    ], []);

    const agentVisibleColsCount = useMemo(() => {
        let count = 0;
        if (agentVisibleColumns.ip_address) count++;
        if (agentVisibleColumns.hostname) count++;
        if (agentVisibleColumns.status) count++;
        if (agentVisibleColumns.connection_id) count++;
        if (agentVisibleColumns.db_monitoring) count++;
        return count || 1;
    }, [agentVisibleColumns]);

    // --- Connections State ---
    const [dbConnections, setDbConnections] = useState<any[]>([]);
    const [loadingConnections, setLoadingConnections] = useState(false);
    const [connTotalItems, setConnTotalItems] = useState(0);
    const [connTotalPages, setConnTotalPages] = useState(0);
    const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
    const [selectedConnection, setSelectedConnection] = useState<any>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [connectionToDelete, setConnectionToDelete] = useState<any>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [connSearchTerm, setConnSearchTerm] = useState('');
    const [debouncedConnSearch, setDebouncedConnSearch] = useState('');
    const [connCurrentPage, setConnCurrentPage] = useState<number>(1);
    const [connItemsPerPage, setConnItemsPerPage] = useState<number>(30);
    const connStartIndex = (connCurrentPage - 1) * connItemsPerPage;
    const connEndIndex = connStartIndex + connItemsPerPage;

    // Connections Column Visibility
    const [connVisibleColumns, setConnVisibleColumns] = useState<Record<ConnColumnKey, boolean>>({
        name: true,
        db_type: true,
        host: true,
        port: true,
        role: true,
        agent_ids: true,
        actions: true,
    });

    const connColumnOptions: ColumnOption<ConnColumnKey>[] = useMemo(() => [
        { key: 'name', label: 'Connection Name' },
        { key: 'db_type', label: 'DB Type' },
        { key: 'host', label: 'Host' },
        { key: 'port', label: 'Port' },
        { key: 'role', label: 'Role' },
        { key: 'agent_ids', label: 'Agent IDs' },
        { key: 'actions', label: 'Actions' },
    ], []);

    const connVisibleColsCount = useMemo(() => {
        let count = 0;
        if (connVisibleColumns.name) count++;
        if (connVisibleColumns.db_type) count++;
        if (connVisibleColumns.host) count++;
        if (connVisibleColumns.port) count++;
        if (connVisibleColumns.role) count++;
        if (connVisibleColumns.agent_ids) count++;
        if (connVisibleColumns.actions) count++;
        return count || 1;
    }, [connVisibleColumns]);

    const handleConnPageChange = (page: number): void => {
        if (page >= 1 && page <= connTotalPages) {
            setConnCurrentPage(page);
        }
    };

    const handleConnItemsPerPageChange = (value: number): void => {
        setConnItemsPerPage(value);
        setConnCurrentPage(1);
    };

    // --- Agents Logic ---
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);

    const fetchAgents = async (skip: number = (currentPage - 1) * itemsPerPage, limit: number = itemsPerPage) => {
        try {
            setLoadingAgents(true);
            const queryObj: any = {
                ...(level?.id ? { client_id: level.id } : {}),
            };

            if (debouncedSearch) {
                queryObj.$or = [
                    { agent_name: { "$ilike": `%${debouncedSearch}%` } },
                    { agent_ip: { "$ilike": `%${debouncedSearch}%` } },
                ];
            }

            const params: any = {
                limit: limit,
                skip: skip,
                query: JSON.stringify(queryObj)
            };
            if (level?.id) {
                params.client_id = level.id;
            }
            const response = await baseapi.get('/db_monitor/agents/', { params });
            if (response.data.status) {
                setAgentData(response.data.data);
                setTotalPages(Math.ceil(response.data.total / limit));
                setTotalItems(response.data.total);
            } else {
                setAgentData([]);
                setTotalPages(0);
                setTotalItems(0);
            }
        } catch (error: any) {
            showError(error.message || 'Failed to fetch agents');
            setAgentData([]);
            setTotalPages(0);
            setTotalItems(0);
        } finally {
            setLoadingAgents(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'agents') {
            fetchAgents((currentPage - 1) * itemsPerPage, itemsPerPage);
        }
    }, [currentPage, itemsPerPage, debouncedSearch, level?.id, activeTab]);

    const handleRefreshAgents = () => {
        setSearchTerm('');
        setDebouncedSearch('');
        setCurrentPage(1);
        fetchAgents(0, itemsPerPage);
    };

    const handlePageChange = (page: number): void => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleItemsPerPageChange = (value: number): void => {
        setItemsPerPage(value);
        setCurrentPage(1);
    };

    const handleToggle = async (agentId: string, currentStatus: boolean) => {
        try {
            const newStatus = !currentStatus;
            setAgentData(prev => prev.map(agent =>
                agent.agent_id === agentId ? { ...agent, db_monitor_enabled: newStatus } : agent
            ));
            const response = await baseapi.patch(`/db-monitor/agents/${agentId}/toggle`, {
                db_monitor_enabled: newStatus
            });
            if (response.data.status) {
                showSuccess(response.data.message || 'Updated successfully');
            } else {
                setAgentData(prev => prev.map(agent =>
                    agent.agent_id === agentId ? { ...agent, db_monitor_enabled: currentStatus } : agent
                ));
                showError(response.data.message || 'Failed to update toggle');
            }
        } catch (error: any) {
            setAgentData(prev => prev.map(agent =>
                agent.agent_id === agentId ? { ...agent, db_monitor_enabled: currentStatus } : agent
            ));
            showError(error.message || 'Error occurred while updating');
        }
    };

    // --- Connections Logic ---
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedConnSearch !== connSearchTerm) {
                setDebouncedConnSearch(connSearchTerm);
                setConnCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [connSearchTerm, debouncedConnSearch]);

    const fetchDBConnections = async (skip: number = (connCurrentPage - 1) * connItemsPerPage, limit: number = connItemsPerPage) => {
        if (!level?.id) return;
        setLoadingConnections(true);
        try {
            const queryObj: any = {
                ...(level?.id ? { client_id: level.id } : {}),
            };

            if (debouncedConnSearch) {
                queryObj.$or = [
                    { name: { "$ilike": `%${debouncedConnSearch}%` } },
                    { db_type: { "$ilike": `%${debouncedConnSearch}%` } },
                    { db_host: { "$ilike": `%${debouncedConnSearch}%` } },
                    { role: { "$ilike": `%${debouncedConnSearch}%` } },
                ];
            }

            const params: any = {
                limit: limit,
                skip: skip,
                query: JSON.stringify(queryObj),
                client_id: level.id
            };
            const response = await baseapi.get("/db_monitor/connections/", { params });
            if (response.data?.status && Array.isArray(response.data.data)) {
                setDbConnections(response.data.data);
                setConnTotalPages(Math.ceil((response.data.total ?? response.data.data.length) / limit));
                setConnTotalItems(response.data.total ?? response.data.data.length);
            } else if (Array.isArray(response.data)) {
                setDbConnections(response.data);
                setConnTotalPages(Math.ceil(response.data.length / limit));
                setConnTotalItems(response.data.length);
            } else if (response.data && Array.isArray(response.data.data)) {
                setDbConnections(response.data.data);
                setConnTotalPages(Math.ceil((response.data.total ?? response.data.data.length) / limit));
                setConnTotalItems(response.data.total ?? response.data.data.length);
            } else {
                setDbConnections([]);
                setConnTotalPages(0);
                setConnTotalItems(0);
            }
        } catch (e: any) {
            showError(e.message || "Failed to load DB connections");
            setDbConnections([]);
            setConnTotalPages(0);
            setConnTotalItems(0);
        } finally {
            setLoadingConnections(false);
        }
    };

    useEffect(() => {
        if (activeTab === "connections" && level?.id) {
            fetchDBConnections((connCurrentPage - 1) * connItemsPerPage, connItemsPerPage);
        }
    }, [activeTab, level?.id, connCurrentPage, connItemsPerPage, debouncedConnSearch]);

    const handleRefreshConnections = () => {
        setConnSearchTerm('');
        setDebouncedConnSearch('');
        setConnCurrentPage(1);
        fetchDBConnections(0, connItemsPerPage);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (openMenuId && !target.closest(`[data-menu-id="${openMenuId}"]`) && !target.closest('[data-action-menu]')) {
                setOpenMenuId(null);
            }
        };
        const handleScroll = () => {
            if (openMenuId) setOpenMenuId(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [openMenuId]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: string | number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition({ top: rect.bottom + 4, left: Math.max(10, rect.left - 120) });
        setOpenMenuId(prev => (prev === String(id) ? null : String(id)));
    };

    const handleDeleteClick = (conn: any) => {
        setConnectionToDelete(conn);
        setDeleteModalOpen(true);
        setOpenMenuId(null);
    };

    const confirmDeleteConnection = async () => {
        if (!connectionToDelete) return;
        try {
            setIsDeleting(true);
            const response = await baseapi.delete(`/db_monitor/connections/${connectionToDelete.id}`, { params: { client_id: level?.id } });
            if (response.data || response.status === 200 || response.status === 204) {
                showSuccess("DB Connection deleted successfully");
                fetchDBConnections((connCurrentPage - 1) * connItemsPerPage, connItemsPerPage);
            } else {
                showError("Failed to delete DB connection");
            }
        } catch (error: any) {
            showError(error.message || "Failed to delete DB connection");
        } finally {
            setIsDeleting(false);
            setDeleteModalOpen(false);
            setConnectionToDelete(null);
        }
    };

    return (
        <div className="animate-in fade-in duration-300 w-full flex flex-col bg-transparent text-gray-900 dark:text-gray-200">
            <div className="w-full mx-auto">
                {/* Header & Tabs */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-gray-200 dark:border-gray-700 mb-4 pb-2 shrink-0 gap-3">
                    <div className="flex gap-2 sm:gap-4">
                        <button
                            onClick={() => setActiveTab("agents")}
                            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-[9px] cursor-pointer flex items-center gap-2 ${activeTab === "agents" ? "border-orange-500 text-orange-600 dark:text-orange-500" : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"}`}
                        >
                            <span>Agents</span>
                            {!loadingAgents && totalItems !== undefined && (
                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${activeTab === "agents" ? "bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400" : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"}`}>
                                    {totalItems}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab("connections")}
                            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-[9px] cursor-pointer flex items-center gap-2 ${activeTab === "connections" ? "border-orange-500 text-orange-600 dark:text-orange-500" : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"}`}
                        >
                            <span>Connections</span>
                           
                        </button>
                    </div>

                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                        {activeTab === "agents" && (
                            <>
                                <button
                                    onClick={handleRefreshAgents}
                                    disabled={loadingAgents}
                                    className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer shrink-0"
                                    title="Refresh"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${loadingAgents ? 'animate-spin' : ''}`} />
                                </button>
                                <div className="relative flex-1 sm:w-56 min-w-[130px]">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                                    <input
                                        type="text"
                                        placeholder="Search agents..."
                                        className="w-full bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 focus:border-orange-500 focus:bg-white dark:focus:bg-gray-800 outline-none transition-all"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                                <div className="shrink-0">
                                    <ColumnVisibilityDropdown
                                        columns={agentColumnOptions}
                                        visibleColumns={agentVisibleColumns}
                                        onChange={setAgentVisibleColumns}
                                    />
                                </div>
                            </>
                        )}
                        {activeTab === "connections" && (
                            <>
                                <button
                                    onClick={handleRefreshConnections}
                                    disabled={loadingConnections}
                                    className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer shrink-0"
                                    title="Refresh"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${loadingConnections ? 'animate-spin' : ''}`} />
                                </button>
                                <div className="relative flex-1 sm:w-56 min-w-[130px]">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                                    <input
                                        type="text"
                                        placeholder="Search connections..."
                                        className="w-full bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 focus:border-orange-500 focus:bg-white dark:focus:bg-gray-800 outline-none transition-all"
                                        value={connSearchTerm}
                                        onChange={(e) => {
                                            setConnSearchTerm(e.target.value);
                                            setConnCurrentPage(1);
                                        }}
                                    />
                                </div>
                                <div className="shrink-0">
                                    <ColumnVisibilityDropdown
                                        columns={connColumnOptions}
                                        visibleColumns={connVisibleColumns}
                                        onChange={setConnVisibleColumns}
                                    />
                                </div>
                                <button
                                    onClick={() => {
                                        setSelectedConnection(null);
                                        setIsConnectionModalOpen(true);
                                    }}
                                    className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-colors shadow-lg shadow-orange-900/20 cursor-pointer shrink-0 whitespace-nowrap"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    <span>Add Connection</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Content */}
                {activeTab === "agents" ? (
                    <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-t-xl  rounded-b-xl overflow-hidden shadow-sm dark:shadow-2xl mt-4">
                        <ScrollContainer
                            className="overflow-x-auto custom-scroll cursor-grab active:cursor-grabbing"
                            horizontal={true}
                            vertical={false}
                            hideScrollbars={false}
                        >
                            <table className="w-full text-left text-xs border-collapse">
                                <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
                                    <tr className="border-b border-gray-200 dark:border-gray-700/50">
                                        {agentVisibleColumns.ip_address && (
                                            <th className="px-2 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest select-none whitespace-nowrap text-left">
                                                <div className="flex items-center gap-2 pl-2">
                                                    
                                                    IP Address
                                                </div>
                                            </th>
                                        )}
                                        {agentVisibleColumns.hostname && (
                                            <th className="px-2 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest select-none whitespace-nowrap text-center">
                                                <div className="flex items-center gap-2 justify-center">
                                                   
                                                    Hostname
                                                </div>
                                            </th>
                                        )}
                                        {agentVisibleColumns.status && (
                                            <th className="px-2 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest select-none whitespace-nowrap text-center">
                                                <div className="flex items-center gap-2 justify-center">
                                                   
                                                    Status
                                                </div>
                                            </th>
                                        )}
                                        {agentVisibleColumns.connection_id && (
                                            <th className="px-2 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest select-none whitespace-nowrap text-center">
                                                <div className="flex items-center gap-2 justify-center">
                                                    
                                                    Connection ID
                                                </div>
                                            </th>
                                        )}
                                        {agentVisibleColumns.db_monitoring && (
                                            <th className="px-2 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest select-none whitespace-nowrap text-center">
                                                <div className="flex items-center gap-2 justify-center">
                                                    
                                                    DB Monitoring
                                                </div>
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30 bg-white dark:bg-gray-900/20 text-xs">
                                    {loadingAgents ? (
                                        <tr>
                                            <td colSpan={agentVisibleColsCount} className="py-16 text-center text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest italic">
                                                <div className="flex items-center justify-center gap-2">
                                                    <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                                                    Loading agents...
                                                </div>
                                            </td>
                                        </tr>
                                    ) : agentData.length === 0 ? (
                                        <tr>
                                            <td colSpan={agentVisibleColsCount} className="py-16 text-center text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest italic">
                                                No agents found
                                            </td>
                                        </tr>
                                    ) : (
                                        agentData.map((agent: any) => (
                                            <tr key={agent.agent_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-100 dark:border-gray-700/30">
                                                {agentVisibleColumns.ip_address && (
                                                    <td className="px-2 py-1.5 font-mono text-left text-[11px] text-blue-600 dark:text-blue-400 font-bold pl-4">
                                                        {agent.agent_ip || '-'}
                                                    </td>
                                                )}
                                                {agentVisibleColumns.hostname && (
                                                    <td className="px-2 py-1.5 font-mono text-center text-[11px] text-gray-800 dark:text-gray-100 truncate max-w-[200px]" title={agent.agent_name || '-'}>
                                                        {agent.agent_name || '-'}
                                                    </td>
                                                )}
                                                {agentVisibleColumns.status && (
                                                    <td className="px-2 py-1.5 text-center">
                                                        <span className={`text-[10px] font-medium capitalize px-2 py-0.5 rounded-full border ${
                                                            agent.agent_status === 'online'
                                                                ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/20'
                                                                : 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/20'
                                                        }`}>
                                                            {agent.agent_status || '-'}
                                                        </span>
                                                    </td>
                                                )}
                                                {agentVisibleColumns.connection_id && (
                                                    <td className="px-2 py-1.5 text-center">
                                                        {agent.assignment?.db_monitor_connection_id ? (
                                                            <span className="text-[10px] font-mono text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 px-2 py-0.5 rounded">
                                                                {agent.assignment.db_monitor_connection_id}
                                                            </span>
                                                        ) : (
                                                            <span className="text-[10px] text-gray-400 dark:text-gray-600 font-bold">-</span>
                                                        )}
                                                    </td>
                                                )}
                                                {agentVisibleColumns.db_monitoring && (
                                                    <td className="px-2 py-1.5 text-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleToggle(agent.agent_id, !!agent.db_monitor_enabled)}
                                                            className={`relative inline-flex h-4 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 transition-colors ease-in-out duration-200 ${
                                                                agent.db_monitor_enabled ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'
                                                            }`}
                                                        >
                                                            <span className="sr-only">Toggle DB Monitoring</span>
                                                            <span
                                                                className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                                    agent.db_monitor_enabled ? 'translate-x-1.5' : '-translate-x-1.5'
                                                                }`}
                                                            />
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </ScrollContainer>

                        {/* Table Footer */}
                        <div className="bg-gray-50 dark:bg-gray-800/50 border rounded-b-xl border-gray-200 dark:border-gray-700 px-3 py-2 flex flex-col sm:flex-row items-center justify-between gap-3">
                            <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-start">
                                <ItemsPerPageDropdown
                                    value={itemsPerPage}
                                    onChange={(val: any) => handleItemsPerPageChange(val)}
                                />
                                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">
                                    <span className="text-gray-800 dark:text-gray-200">{totalItems > 0 ? startIndex + 1 : 0}</span> - <span className="text-gray-800 dark:text-gray-200">{Math.min(endIndex, totalItems)}</span> OF {totalItems}
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-200/50 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700/50 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer"><ChevronsLeft className="w-4 h-4" /></button>
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-200/50 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700/50 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer"><ChevronLeft className="w-4 h-4" /></button>
                                <div className="px-2.5 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1 shadow-sm shadow-orange-900/20">{currentPage}</div>
                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-200/50 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700/50 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer"><ChevronRight className="w-4 h-4" /></button>
                                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0} className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-200/50 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700/50 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer"><ChevronsRight className="w-4 h-4" /></button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-t-xl rounded-b-xl overflow-hidden shadow-sm dark:shadow-2xl mt-4">
                        <ScrollContainer
                            className="overflow-x-auto custom-scroll cursor-grab active:cursor-grabbing"
                            horizontal={true}
                            vertical={false}
                            hideScrollbars={false}
                        >
                            <table className="w-full text-left text-xs border-collapse">
                                <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
                                    <tr className="border-b border-gray-200 dark:border-gray-700/50">
                                        {connVisibleColumns.name && (
                                            <th className="px-2 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest select-none whitespace-nowrap text-center">
                                                <div className="flex items-center gap-2 pl-2">
                                                    
                                                    Connection Name
                                                </div>
                                            </th>
                                        )}
                                        {connVisibleColumns.db_type && (
                                            <th className="px-2 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest select-none whitespace-nowrap text-center">
                                                <div className="flex items-center gap-2 justify-center">
                                                    
                                                    DB Type
                                                </div>
                                            </th>
                                        )}
                                        {connVisibleColumns.host && (
                                            <th className="px-2 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest select-none whitespace-nowrap text-center">
                                                <div className="flex items-center gap-2 justify-center">
                                                   
                                                    Host
                                                </div>
                                            </th>
                                        )}
                                        {connVisibleColumns.port && (
                                            <th className="px-2 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest select-none whitespace-nowrap text-center">
                                                <div className="flex items-center gap-2 justify-center">

                                                    Port
                                                </div>
                                            </th>
                                        )}
                                        {connVisibleColumns.role && (
                                            <th className="px-2 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest select-none whitespace-nowrap text-center">
                                                <div className="flex items-center gap-2 justify-center">
                                                    
                                                    Role
                                                </div>
                                            </th>
                                        )}
                                       
                                        {connVisibleColumns.actions && (
                                            <th className="px-2 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest select-none whitespace-nowrap text-center">
                                                <div className="flex items-center gap-2 justify-center">
                                                    
                                                    Actions
                                                </div>
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30 bg-white dark:bg-gray-900/20 text-xs">
                                    {loadingConnections ? (
                                        <tr>
                                            <td colSpan={connVisibleColsCount} className="py-16 text-center text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest italic">
                                                <div className="flex items-center justify-center gap-2">
                                                    <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                                                    Loading connections...
                                                </div>
                                            </td>
                                        </tr>
                                    ) : dbConnections.length === 0 ? (
                                        <tr>
                                            <td colSpan={connVisibleColsCount} className="py-16 text-center text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest italic">
                                                No DB connections found
                                            </td>
                                        </tr>
                                    ) : (
                                        dbConnections.map((conn, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-100 dark:border-gray-700/30">
                                                {connVisibleColumns.name && (
                                                    <td className="px-2 py-1.5 text-[11px] font-semibold text-gray-900 dark:text-white pl-4">
                                                        {conn.name || '-'}
                                                    </td>
                                                )}
                                                {connVisibleColumns.db_type && (
                                                    <td className="px-2 py-1.5 text-center">
                                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-tighter bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20">
                                                            {conn.db_type || '-'}
                                                        </span>
                                                    </td>
                                                )}
                                                {connVisibleColumns.host && (
                                                    <td className="px-2 py-1.5 text-center font-mono text-[10px] text-gray-600 dark:text-gray-300">
                                                        {conn.db_host || '-'}
                                                    </td>
                                                )}
                                                {connVisibleColumns.port && (
                                                    <td className="px-2 py-1.5 text-center font-mono text-[10px] text-gray-600 dark:text-gray-300">
                                                        {conn.db_port || '-'}
                                                    </td>
                                                )}
                                                {connVisibleColumns.role && (
                                                    <td className="px-2 py-1.5 text-center text-[10px] text-gray-600 dark:text-gray-300 uppercase tracking-tighter font-semibold">
                                                        {conn.role || '-'}
                                                    </td>
                                                )}
                                               
                                                {connVisibleColumns.actions && (
                                                    <td className="px-2 py-1.5 text-center whitespace-nowrap">
                                                        <button
                                                            data-menu-id={conn.id}
                                                            onClick={(e) => handleMenuOpen(e, conn.id)}
                                                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
                                                        >
                                                            <CircleEllipsis className="w-4.5 h-4.5" />
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </ScrollContainer>

                        {/* Table Footer */}
                        <div className="bg-gray-50 dark:bg-gray-800/50 border rounded-b-xl border-gray-200 dark:border-gray-700 px-3 py-2 flex flex-col sm:flex-row items-center justify-between gap-3">
                            <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-start">
                                <ItemsPerPageDropdown
                                    value={connItemsPerPage}
                                    onChange={(val: any) => handleConnItemsPerPageChange(val)}
                                />
                                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">
                                    <span className="text-gray-800 dark:text-gray-200">{connTotalItems > 0 ? connStartIndex + 1 : 0}</span> - <span className="text-gray-800 dark:text-gray-200">{Math.min(connEndIndex, connTotalItems)}</span> OF {connTotalItems}
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                <button onClick={() => handleConnPageChange(1)} disabled={connCurrentPage === 1} className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-200/50 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700/50 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer"><ChevronsLeft className="w-4 h-4" /></button>
                                <button onClick={() => handleConnPageChange(connCurrentPage - 1)} disabled={connCurrentPage === 1} className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-200/50 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700/50 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer"><ChevronLeft className="w-4 h-4" /></button>
                                <div className="px-2.5 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1 shadow-sm shadow-orange-900/20">{connCurrentPage}</div>
                                <button onClick={() => handleConnPageChange(connCurrentPage + 1)} disabled={connCurrentPage === connTotalPages || connTotalPages === 0} className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-200/50 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700/50 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer"><ChevronRight className="w-4 h-4" /></button>
                                <button onClick={() => handleConnPageChange(connTotalPages)} disabled={connCurrentPage === connTotalPages || connTotalPages === 0} className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-200/50 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700/50 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer"><ChevronsRight className="w-4 h-4" /></button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <DBConnectionModal
                isOpen={isConnectionModalOpen}
                onClose={() => {
                    setIsConnectionModalOpen(false);
                    setSelectedConnection(null);
                }}
                onSuccess={() => fetchDBConnections((connCurrentPage - 1) * connItemsPerPage, connItemsPerPage)}
                connectionToEdit={selectedConnection}
                client_id={level?.id}
            />

            <DeleteConfirmationModal
                open={deleteModalOpen}
                isDeleting={isDeleting}
                connectionName={connectionToDelete?.name || 'DB Connection'}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setConnectionToDelete(null);
                }}
                onConfirm={confirmDeleteConnection}
            />

            {/* Actions Portal Menu */}
            {openMenuId && createPortal(
                (() => {
                    const activeConn = dbConnections.find(c => String(c.id) === openMenuId);
                    if (!activeConn) return null;
                    return (
                        <div
                            data-action-menu
                            className="fixed w-44 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[9999] overflow-hidden py-1 animate-in fade-in zoom-in-95 duration-100"
                            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                        >
                            <button
                                onClick={() => {
                                    setOpenMenuId(null);
                                    setSelectedConnection(activeConn);
                                    setIsConnectionModalOpen(true);
                                }}
                                className="w-full text-left px-4 py-2.5 text-[11px] font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors cursor-pointer"
                            >
                                <Pencil className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /> Edit Connection
                            </button>
                            <button
                                onClick={() => {
                                    handleDeleteClick(activeConn);
                                }}
                                className="w-full text-left px-4 py-2.5 text-[11px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-3 font-bold cursor-pointer"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Delete Connection
                            </button>
                        </div>
                    );
                })(),
                document.body
            )}
        </div>
    );
};

export default DbSettings;
